import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { SegmentedControl, StatusBadge } from '../../../ui';
import DashCard, { CardEmpty, CardLink } from '../shared/DashCard';
import IconChip from '../shared/IconChip';
import { assignmentLink } from '../studentHomeRules';

const AssignmentRow = ({ assignment }) => (
  <li>
    <Link
      to={assignmentLink(assignment)}
      className="tw-flex tw-items-center tw-gap-3 tw-rounded-sm tw-px-2 tw-py-2 tw-text-content tw-no-underline tw-transition-colors hover:tw-bg-surface-sunken hover:tw-text-content tw-focus-ring-inset"
    >
      <IconChip icon={FileText} tone="neutral" size={32} />
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-line-clamp-2 tw-text-[0.8125rem] tw-font-semibold tw-leading-snug">{assignment.title}</span>
        <span className="tw-block tw-text-[0.6875rem] tw-text-content-subtle">{assignment.course}</span>
      </span>
      <StatusBadge status={assignment.status === 'submitted' ? 'success' : 'warning'} className="tw-shrink-0">
        {assignment.status === 'submitted' ? 'Submitted' : 'Pending'}
      </StatusBadge>
    </Link>
  </li>
);

const AssignmentsCard = ({ assignments, className }) => {
  const [view, setView] = useState('all');

  const counts = useMemo(() => ({
    pending: assignments.filter((a) => a.status === 'pending').length,
    submitted: assignments.filter((a) => a.status === 'submitted').length,
  }), [assignments]);

  const shown = view === 'all' ? assignments : assignments.filter((a) => a.status === view);

  return (
    <DashCard
      title="Assignments"
      className={className}
      action={(
        <SegmentedControl
          label="Show assignments"
          size="xs"
          value={view}
          onChange={setView}
          options={[
            { value: 'all', label: 'All' },
            { value: 'pending', label: 'Pending', count: counts.pending },
            { value: 'submitted', label: 'Submitted', count: counts.submitted },
          ]}
        />
      )}
    >
      {shown.length > 0 ? (
        <ul className="tw-m-0 tw--mx-2 tw-flex tw-list-none tw-flex-col tw-gap-0.5 tw-p-0">
          {shown.map((assignment) => <AssignmentRow key={assignment.id} assignment={assignment} />)}
        </ul>
      ) : (
        <CardEmpty>No assignments here.</CardEmpty>
      )}
      <div className="tw-mt-auto tw-pt-3">
        <div className="tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-pt-2.5 tw-text-center">
          <CardLink to="/s/learning" state={{ tab: 'assignments' }}>View all assignments</CardLink>
        </div>
      </div>
    </DashCard>
  );
};

export default AssignmentsCard;
